import {createSlice, type SliceSelectors} from '@reduxjs/toolkit'
import {LOCATION_CHANGE} from 'redux-first-history'

import {eq} from '@shared/helpers/lang'
import {qs} from '@shared/helpers/queryString'

import {
    applyAddress,
    changeMemo,
    changeZipCodeSearchInfo,
    checkDoneCondition,
    clearDeliveryManagerStore,
    closeAddressForm,
    confirmAddressForm,
    confirmDeliveryInfo,
    deleteAddress,
    deletePresetMemo,
    getAddressBook,
    getPresetMemoList,
    goAddressForm,
    goBackToSearch,
    handlePersonalCustomsCodeValidationError,
    initDeliveryManagerStore,
    openDeliveryManager,
    searchZipcode,
    selectDeliveryAddress,
    selectZipCode,
    sendZipCodeResult,
    toggleParticularMemo,
    toggleSaveAsDefaultMemo,
} from '@web/actions/deliveryManager'
import {APIStatus, ChangeDeliveryRequestFailWindowMaintain, maxAddressBookCount} from '@web/core/constants'
import {PAY_HOME_M} from '@web/core/urls'
import {examineUrlSafety} from '@web/helpers/path'
import {
    changeDeliveryAddress,
    changeOrderInfo,
    editPersonalCustomsCode,
    toggleVirtualPhoneNumber,
    DELIVERY_MANAGER_STEP,
    DeliveryManagerState,
    getEmptyAddressSearch,
    getInitialState,
} from '@web/slices/deliveryManager/slice'
import {getLocationState, LocationChangeAction} from '@web/slices/router'

import type {DeliveryManagerReducers, MakeCreateSliceOptions} from './interfaces'

export default (options?: MakeCreateSliceOptions) =>
    createSlice<DeliveryManagerState, DeliveryManagerReducers, 'deliveryManager', SliceSelectors<DeliveryManagerState>>(
        {
            name: 'deliveryManager',
            initialState: getInitialState(),
            reducers: {},
            extraReducers: (builder) => {
                builder
                    .addCase<typeof LOCATION_CHANGE, LocationChangeAction>(LOCATION_CHANGE, (state, {payload}) => {
                        const {location} = payload
                        const {rurl, allowsixdigit, key, closeType} = qs.parse(location?.search ?? '')

                        // 닫기 옵션
                        state.options.returnUrl = rurl ? examineUrlSafety(rurl, PAY_HOME_M) : null
                        state.options.postMessageKey = key ?? null
                        if (closeType === 'back' || closeType === 'close') {
                            state.options.closeType = closeType
                        }

                        // 우편번호검색 옵션
                        state.addressSearch.query.allowSixDigitCode = allowsixdigit === 'true'

                        // locationState 의 배송지변경 관련 정보 미러링
                        if (location) {
                            const {deliveryManagerScene, historyCountFromBase} = getLocationState(location)
                            state.status.scene = deliveryManagerScene || null
                            state.status.historyCountFromBase = historyCountFromBase || 0
                        }
                    })
                    .addCase(openDeliveryManager.fulfilled, (state, {payload}) => {
                        state.options.type = payload.type
                    })
                    .addCase(initDeliveryManagerStore.fulfilled, (state, {payload}) => {
                        state.options.type = payload.type
                        state.address = payload.address
                        state.deliveryAddress = payload.deliveryAddress
                        state.deliveryAddressBook.selectedHash = payload.orderInfo?.originalAddressHash || null
                        state.orderInfo = payload.orderInfo
                        state.addressSearch.query = payload.searchQuery
                    })
                    .addCase(initDeliveryManagerStore.rejected, (state) => {
                        state.status.step = DELIVERY_MANAGER_STEP.ERROR
                    })
                    .addCase(changeZipCodeSearchInfo.pending, (state) => {
                        state.addressSearch.query.keyword = ''
                    })
                    // 우편번호 검색버튼 클릭 시 검색어 저장
                    .addCase(changeZipCodeSearchInfo.fulfilled, (state, {payload}) => {
                        state.addressSearch.query.keyword = payload.keyword
                        state.addressSearch.query.page = payload.page
                        state.addressSearch.query.regionCode = payload.regionCode
                    })
                    .addCase(searchZipcode.pending, (state) => {
                        state.addressSearch.status = APIStatus.FETCHING
                        state.addressSearch.totalCount = 0
                        state.addressSearch.regionCountResult = []
                        state.addressSearch.addressResult = []
                    })
                    .addCase(searchZipcode.fulfilled, (state, {payload}) => {
                        state.addressSearch.status = APIStatus.RESOLVED
                        state.addressSearch.totalCount = payload.totalCount
                        state.addressSearch.regionCountResult = payload.regionCountItems
                        state.addressSearch.addressResult = payload.results
                    })
                    .addCase(searchZipcode.rejected, (state) => {
                        state.addressSearch.status = APIStatus.REJECTED
                    })
                    .addCase(selectZipCode.fulfilled, (state, {payload}) => {
                        state.address = payload || null
                    })
                    .addCase(goBackToSearch.fulfilled, (state, {payload}) => {
                        state.addressSearch.query = payload
                    })
                    .addCase(sendZipCodeResult.fulfilled, (state, {payload, type}) => {
                        state.address = payload?.address || null
                        state.deliveryAddress = payload?.deliveryAddress || null

                        const openType = state.options.type || ''
                        if (checkDoneCondition({openType, actionType: type})) {
                            state.status.step = DELIVERY_MANAGER_STEP.APPLYING
                        }
                    })
                    .addCase(confirmAddressForm.fulfilled, (state, {payload, type}) => {
                        const openType = state.options.type || ''
                        if (checkDoneCondition({openType, actionType: type})) {
                            state.deliveryAddress = payload
                            state.status.step = DELIVERY_MANAGER_STEP.APPLYING
                        } else {
                            state.addressSearch = getEmptyAddressSearch()
                            state.address = null
                            state.deliveryAddress = null
                        }
                    })
                    .addCase(closeAddressForm.fulfilled, (state) => {
                        state.addressSearch = getEmptyAddressSearch()
                        state.address = null
                        state.deliveryAddress = null
                    })
                    .addCase(getAddressBook.pending, (state) => {
                        state.deliveryAddressBook.status = APIStatus.FETCHING
                    })
                    .addCase(getAddressBook.fulfilled, (state, {payload}) => {
                        state.deliveryAddressBook.status = APIStatus.RESOLVED
                        state.deliveryAddressBook.deliveryAddressList = payload.list
                        state.deliveryAddressBook.isAddible = payload.isAddible
                    })
                    .addCase(getAddressBook.rejected, (state) => {
                        state.deliveryAddressBook.status = APIStatus.REJECTED
                    })
                    .addCase(selectDeliveryAddress.fulfilled, (state, {payload, type}) => {
                        state.deliveryAddressBook.selectedHash = payload.hash

                        const openType = state.options.type || ''
                        if (checkDoneCondition({openType, actionType: type})) {
                            state.deliveryAddress = payload
                            state.status.step = DELIVERY_MANAGER_STEP.APPLYING
                        }
                    })
                    .addCase(confirmDeliveryInfo.fulfilled, (state, {payload}) => {
                        state.deliveryAddress = payload
                        state.status.step = DELIVERY_MANAGER_STEP.APPLYING
                    })
                    .addCase(goAddressForm.fulfilled, (state, {payload}) => {
                        state.deliveryAddress = payload
                    })
                    .addCase(changeDeliveryAddress, (state, {payload}) => {
                        state.deliveryAddress = payload
                    })
                    .addCase(changeOrderInfo, (state, {payload}) => {
                        state.orderInfo = payload
                    })
                    .addCase(deleteAddress.fulfilled, (state, {payload}) => {
                        const eqHash = eq(payload.hash)
                        state.deliveryAddressBook.deliveryAddressList =
                            state.deliveryAddressBook.deliveryAddressList.map((address) => {
                                return eqHash(address.hash) ? {...address, isDeleted: true} : address
                            })

                        const isSavedAddress = ({
                            isRegistered,
                            isDeleted,
                        }: {
                            isRegistered: boolean
                            isDeleted: boolean
                        }) => isRegistered && !isDeleted
                        const savedAddressCount =
                            state.deliveryAddressBook.deliveryAddressList.filter(isSavedAddress).length
                        state.deliveryAddressBook.isAddible = savedAddressCount < maxAddressBookCount
                    })
                    .addCase(getPresetMemoList.fulfilled, (state, {payload}) => {
                        state.memoBook = payload
                    })
                    .addCase(deletePresetMemo.fulfilled, () => {
                        /* empty */
                    })
                    .addCase(changeMemo, (state, {payload}) => {
                        const {productOrderNo, value, type} = payload
                        const isParticularInput = state.orderInfo.deliveryMemo.options.particularMemo.used

                        if (!isParticularInput && state.orderInfo.deliveryMemo.memoList[0]) {
                            state.orderInfo.deliveryMemo.memoList[0].value = value
                            state.orderInfo.deliveryMemo.memoList[0].type = type
                        } else {
                            state.orderInfo.deliveryMemo.memoList = state.orderInfo.deliveryMemo.memoList.map(
                                (memo) => {
                                    if (memo.product?.productOrderNo === productOrderNo) {
                                        return {value, type, product: memo.product}
                                    } else {
                                        return memo
                                    }
                                },
                            )
                        }
                    })
                    .addCase(toggleParticularMemo, (state) => {
                        state.orderInfo.deliveryMemo.options.particularMemo.used =
                            !state.orderInfo.deliveryMemo.options.particularMemo.used
                    })
                    .addCase(toggleSaveAsDefaultMemo, (state) => {
                        state.orderInfo.deliveryMemo.options.saveAsDefaultMemo =
                            !state.orderInfo.deliveryMemo.options.saveAsDefaultMemo
                    })
                    .addCase(toggleVirtualPhoneNumber, (state) => {
                        state.orderInfo.virtualPhoneNumber.used = !state.orderInfo.virtualPhoneNumber.used
                    })
                    .addCase(editPersonalCustomsCode, (state, {payload}) => {
                        state.orderInfo.personalCustomsCode = {
                            ...state.orderInfo.personalCustomsCode,
                            newCode: payload,
                            errorStatus: 'IDLE',
                        }
                    })
                    .addCase(handlePersonalCustomsCodeValidationError.fulfilled, (state, {payload}) => {
                        if (state.orderInfo.personalCustomsCode) {
                            state.orderInfo.personalCustomsCode.errorStatus = payload
                        }
                    })
                    .addCase(applyAddress.fulfilled, (state) => {
                        state.status.step = DELIVERY_MANAGER_STEP.SUCCESS
                    })
                    .addCase(applyAddress.rejected, (state, {payload}) => {
                        const keepOpened = ChangeDeliveryRequestFailWindowMaintain[payload?.code || '']
                        if (keepOpened) {
                            state.status.step = DELIVERY_MANAGER_STEP.IDLE
                        } else {
                            state.status.step = DELIVERY_MANAGER_STEP.ERROR
                        }
                    })
                    .addCase(clearDeliveryManagerStore, (state) => {
                        const initialState = getInitialState()
                        Object.assign(state, initialState)
                    })
                options?.extraReducers?.(builder)
            },
        },
    )
